import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';

import EditIcon from '@mui/icons-material/Edit';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';

import { ReactComponent as LoadingSpinner } from '../../images/loading-spinner.svg';

import { industryVerticalsAlphabetizedAndSorted } from '../../utils/IndustryVerticals';

import useS3 from '../../hooks/useS3';
import useFetch from '../../hooks/useFetch';

import {
  EditTextField,
  EditSelectField,
  EditDateField,
  EditYesNoField,
  EditRadioField,
  EditIndustryField,
} from './ClientInputFields';

import './ClientInputs.scss';

const EXIT_TYPE_OPTIONS = [
  'Merger and acquisition (M&A)',
  'Initial public offering (IPO)',
  'Don\'t know',
  'Other',
];

const EXIT_TIME_OPTIONS = [
  'Less than 1 year',
  '1 year',
  '2 years',
  '3 years',
  '4 years',
  '5 years or more',
];

const FUNDING_TYPE_OPTIONS = [
  'SAFE Note',
  'Convertible Note',
  'Angel',
  'Pre-Seed',
  'Series Seed',
  'Series A',
  'Series B',
  'Series C',
  'Series D',
  'Series E',
  'Series F',
  'No funding',
];

const CAP_TABLE_PROVIDER_OPTIONS = ['Carta', 'Pulley', 'Shareworks', 'Other'];

const PRE_OR_POST_OPTIONS = ['Pre-money', 'Post-money'];

function isTrueOrFalseOrNull(value) {
  if (value === 1) return true;
  if (value === 0) return false;
  return null;
}

function isOneOrZeroOrNull(value) {
  if (value === true) return 1;
  if (value === false) return 0;
  return null;
}

function ReviewTile({
  label,
  data,
  isEditing,
  editField,
  alwaysShow,
}) {
  const [, s3FileRequest] = useS3();

  if (!alwaysShow && !data && !isEditing) return null;

  if (isEditing && editField) {
    return (
      <div className="client-review-tile">
        {editField}
      </div>
    );
  }

  if (data) {
    return (
      <div className="client-review-tile">
        <p>{label}</p>
        {Array.isArray(data) ? (
          <>
            {data.map((file) => (
              <Button
                key={`${file.uploadType}-${file.fileName}`}
                onClick={() => s3FileRequest({
                  requestType: 'get',
                  key: file.s3Key,
                  onSuccess: (url) => window.open(url),
                })}
              >
                {file.fileName.length >= 30 ? `${file.fileName.slice(0, 30).trim()}...` : file.fileName}
              </Button>
            ))}
          </>
        ) : (
          <p>{data}</p>
        )}
      </div>
    );
  }

  return null;
}

ReviewTile.propTypes = {
  label: PropTypes.string,
  data: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array,
  ]),
  isEditing: PropTypes.bool,
  editField: PropTypes.node,
  alwaysShow: PropTypes.bool,
};

export default function ClientInputs({
  companyToViewData,
  setCompanyToViewData,
  currentAdminName,
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Wiz 1
  const [valuationDate, setValuationDate] = useState('');
  const [whenIsExitLikelyToHappen, setWhenIsExitLikelyToHappen] = useState('');
  const [whatTypeOfExit, setWhatTypeOfExit] = useState('');
  const [industryVertical, setIndustryVertical] = useState(null);
  const [fiscalYearEnd, setFiscalYearEnd] = useState('');
  // Wiz 2
  const [wiz2Data, setWiz2Data] = useState({
    ntmOptionsPerc: 50,
    totalCurrentCash: '',
    currentMonthlyCashBurn: '',
    totalNonConvertibleDebt: '',
    totalConvertibleDebt: '',
    totalCurrentFiscalYearRevenue: '',
    totalNextFiscalYearRevenue: '',
    totalCurrentFiscalYearEBITDA: '',
    totalNextFiscalYearEBITDA: '',
    ltmRevenue: '',
    ntmRevenue: '',
    ltmEBITDA: '',
    ntmEBITDA: '',
  });

  // Wiz 3
  const [chosenFunding, setChosenFunding] = useState('');
  const [currentFundingDate, setCurrentFundingDate] = useState('');
  const [totalAmountRaised, setTotalAmountRaised] = useState('');
  const [totalSafeOrConvertible, setTotalSafeOrConvertible] = useState('');
  const [interestRate, setInterestRate] = useState('');
  const [valuationCap, setValuationCap] = useState('');
  const [preOrPostMoneyNotes, setPreOrPostMoneyNotes] = useState(null);
  const [previousFundingRoundType, setPreviousFundingRoundType] = useState('');
  const [receivedFutureTerms, setReceivedFutureTerms] = useState(null);
  const [
    anySecondaryCommonStockTransactions,
    setAnySecondaryCommonStockTransactions,
  ] = useState(null);
  const [
    secondaryCommonStockTransactionTotalAmount,
    setSecondaryCommonStockTransactionTotalAmount,
  ] = useState('');
  const [
    percentageSecondaryCommonStockSold,
    setPercentageSecondaryCommonStockSold,
  ] = useState('');
  // Wiz 4
  const [capTableProvider, setCapTableProvider] = useState('');
  const [capTableFilesUploaded, setCapTableFilesUploaded] = useState([]);
  const [optionLedgerFilesUploaded, setOptionLedgerFilesUploaded] = useState([]);
  const [financialFilesUpload, setFinancialFilesUploaded] = useState([]);
  const [articlesOfIncorporationUploaded, setArticlesOfIncorporationUploaded] = useState([]);
  const [secondaryTransactionDocs, setSecondaryTransactionDocs] = useState([]);
  const [termsSheets, setTermsSheets] = useState([]);
  const [safeOrConvertibleNoteAgreement, setSafeOrConvertibleNoteAgreement] = useState([]);

  const [, updateTransactionRequest] = useFetch();

  function loadStateFromTransaction(transactionData) {
    if (!transactionData || typeof transactionData !== 'object') return;

    setValuationDate(transactionData.valuationDate ?
      moment(transactionData.valuationDate, 'YYYY-MM-DD').format('MM/DD/YYYY') : '');
    setWhenIsExitLikelyToHappen(transactionData.whenIsExitLikelyToHappen || '');
    setWhatTypeOfExit(transactionData.whatTypeOfExit || '');
    setIndustryVertical(transactionData.industryVertical ? {
      title: transactionData.industryVertical,
      category: transactionData.verticalCategory,
      subcategory: transactionData.verticalSubcategory,
    } : null);
    setWiz2Data({
      ntmOptionsPerc: parseInt(transactionData.ntmOptionsPerc, 10) || 50,
      totalCurrentCash: transactionData.totalCurrentCash || '',
      currentMonthlyCashBurn: transactionData.currentMonthlyCashBurn || '',
      totalNonConvertibleDebt: transactionData.totalNonConvertibleDebt || '',
      totalConvertibleDebt: transactionData.totalConvertibleDebt || '',
      totalCurrentFiscalYearRevenue: transactionData.totalCurrentFiscalYearRevenue || '',
      totalNextFiscalYearRevenue: transactionData.totalNextFiscalYearRevenue || '',
      totalCurrentFiscalYearEBITDA: transactionData.totalCurrentFiscalYearEBITDA || '',
      totalNextFiscalYearEBITDA: transactionData.totalNextFiscalYearEBITDA || '',
      ltmRevenue: transactionData.ltmRevenue || '',
      ntmRevenue: transactionData.ntmRevenue || '',
      ltmEBITDA: transactionData.ltmEBITDA || '',
      ntmEBITDA: transactionData.ntmEBITDA || '',
    });
    setFiscalYearEnd(transactionData.fiscalYearEnd ?
      moment(transactionData.fiscalYearEnd, 'YYYY-MM-DD').format('MM/DD/YYYY') : '');
    setChosenFunding(transactionData.chosenFunding || '');
    setCurrentFundingDate(transactionData.currentFundingDate ?
      moment(transactionData.currentFundingDate, 'YYYY-MM-DD').format('MM/DD/YYYY') : '');
    setTotalSafeOrConvertible(transactionData.totalSafeOrConvertible || '');
    setTotalAmountRaised(transactionData.totalAmountRaised || '');
    setInterestRate(transactionData.interestRate || '');
    setValuationCap(transactionData.valuationCap || '');
    setPreOrPostMoneyNotes(transactionData.preOrPostMoneyNotes || null);
    setPreviousFundingRoundType(transactionData.previousFundingRoundType || '');
    setReceivedFutureTerms(isTrueOrFalseOrNull(transactionData.receivedFutureTerms));
    setAnySecondaryCommonStockTransactions(
      isTrueOrFalseOrNull(transactionData.anySecondaryCommonStockTransactions),
    );
    setSecondaryCommonStockTransactionTotalAmount(
      transactionData.secondaryCommonStockTransactionTotalAmount || '',
    );
    setPercentageSecondaryCommonStockSold(transactionData.percentageSecondaryCommonStockSold || '');
    setCapTableProvider(transactionData.capTableProvider || '');
    setCapTableFilesUploaded(transactionData.documents?.cap_table_files || []);
    if (transactionData.documents?.option_ledger_files) {
      setOptionLedgerFilesUploaded(transactionData.documents.option_ledger_files || []);
    } else {
      setOptionLedgerFilesUploaded('N/A');
    }
    setFinancialFilesUploaded(transactionData.documents?.financial_files || []);
    setArticlesOfIncorporationUploaded(transactionData.documents?.articles_of_incorporation || []);
    setSecondaryTransactionDocs(transactionData.documents?.secondary_transaction || []);
    setTermsSheets(transactionData.documents?.term_sheets || []);
    setSafeOrConvertibleNoteAgreement(transactionData.documents?.safe_or_convertible_note_agreement || []);
  }

  useEffect(() => {
    loadStateFromTransaction(companyToViewData);
  }, [companyToViewData]);

  function buildTransactionPayload() {
    const optionLedgerFiles = Array.isArray(optionLedgerFilesUploaded) ?
      optionLedgerFilesUploaded :
      (companyToViewData.documents?.option_ledger_files || []);

    return {
      companyId: companyToViewData.companyId,
      companyName: companyToViewData.companyName,
      transactionId: companyToViewData.transactionId,
      insertUserId: companyToViewData.insertUserId,
      progress: companyToViewData.progress,
      paymentId: companyToViewData.paymentId,
      transactionDate: companyToViewData.transactionDate,
      adminProgress: companyToViewData.adminProgress,
      lastModifiedBy: currentAdminName,
      valuationDate: valuationDate ?
        moment(valuationDate, 'MM/DD/YYYY').format('YYYY-MM-DD') : null,
      industryVertical: industryVertical ? industryVertical.title : '',
      verticalSubcategory: industryVertical ? industryVertical.subcategory : '',
      verticalCategory: industryVertical ? industryVertical.category : '',
      currentMonthlyCashBurn: wiz2Data.currentMonthlyCashBurn,
      totalConvertibleDebt: wiz2Data.totalConvertibleDebt,
      totalCurrentCash: wiz2Data.totalCurrentCash,
      totalNonConvertibleDebt: wiz2Data.totalNonConvertibleDebt,
      EBITDAChecked: companyToViewData.EBITDAChecked,
      totalCurrentFiscalYearRevenue: wiz2Data.totalCurrentFiscalYearRevenue,
      totalNextFiscalYearRevenue: wiz2Data.totalNextFiscalYearRevenue,
      totalCurrentFiscalYearEBITDA: wiz2Data.totalCurrentFiscalYearEBITDA,
      totalNextFiscalYearEBITDA: wiz2Data.totalNextFiscalYearEBITDA,
      ltmRevenue: wiz2Data.ltmRevenue,
      ntmRevenue: wiz2Data.ntmRevenue,
      ltmEBITDA: wiz2Data.ltmEBITDA,
      ntmEBITDA: wiz2Data.ntmEBITDA,
      fiscalYearEnd: fiscalYearEnd ?
        moment(fiscalYearEnd, 'MM/DD/YYYY').format('YYYY-MM-DD') : null,
      documents: {
        articles_of_incorporation: articlesOfIncorporationUploaded,
        cap_table_files: capTableFilesUploaded,
        option_ledger_files: optionLedgerFiles,
        financial_files: financialFilesUpload,
        safe_or_convertible_note_agreement: safeOrConvertibleNoteAgreement,
        secondary_transaction: secondaryTransactionDocs,
        term_sheets: termsSheets,
      },
      whenIsExitLikelyToHappen,
      whatTypeOfExit,
      previousFundingRoundDate: companyToViewData.previousFundingRoundDate,
      currentFundingDate: currentFundingDate ?
        moment(currentFundingDate, 'MM/DD/YYYY').format('YYYY-MM-DD') : null,
      ntmOptionsPerc: wiz2Data.ntmOptionsPerc,
      capTableProvider,
      secondaryCommonStockTransactionTotalAmount,
      chosenFunding,
      totalAmountRaised,
      totalSafeOrConvertible,
      interestRate,
      valuationCap,
      hasRaisedPreviousRound: companyToViewData.hasRaisedPreviousRound,
      preOrPostMoneyNotes,
      isThisCompanysFirstRound: companyToViewData.isThisCompanysFirstRound,
      previousFundingRoundType,
      receivedFutureTerms: isOneOrZeroOrNull(receivedFutureTerms),
      anySecondaryCommonStockTransactions: isOneOrZeroOrNull(anySecondaryCommonStockTransactions),
      percentageSecondaryCommonStockSold,
    };
  }

  function saveClientInputs() {
    setIsSaving(true);
    updateTransactionRequest({
      url: '/transactions/update-transaction',
      urlIds: [companyToViewData.companyId],
      body: buildTransactionPayload(),
      onSuccess: (response) => {
        const updatedTransaction = response?.Body?.transaction_object;
        if (updatedTransaction && setCompanyToViewData) {
          setCompanyToViewData((prev) => ({
            ...prev,
            transactionData: {
              ...prev.transactionData,
              ...updatedTransaction,
            },
          }));
        }
        setIsEditing(false);
        setIsSaving(false);
      },
      onError: () => {
        setIsSaving(false);
      },
    });
  }

  function handleCancel() {
    loadStateFromTransaction(companyToViewData);
    setIsEditing(false);
  }

  const safeOrConvertibleForCurrentOrPreviousRounds = (
    (previousFundingRoundType === 'SAFE Note') ||
    (previousFundingRoundType === 'Convertible Note') ||
    (chosenFunding === 'SAFE Note') ||
    (chosenFunding === 'Convertible Note')
  );

  const yesNoValue = (value) => {
    if (value === null) return '';
    return value === true ? 'Yes' : 'No';
  };

  return (
    <div className="ClientInputs">
      <div className="client-review-block">
        {currentAdminName && (
          !isEditing && !isSaving ? (
            <IconButton
              className="edit-icon-btn"
              onClick={() => setIsEditing(true)}
            >
              <EditIcon />
            </IconButton>
          ) : (
            <div className="btns-container">
              <Button
                className="save-btn"
                onClick={saveClientInputs}
                disabled={isSaving}
              >
                {!isSaving ? (
                  <>
                    <SaveOutlinedIcon />
                    Save
                  </>
                ) : (
                  <>
                    <LoadingSpinner className="loading-spinner" />
                    Saving
                  </>
                )}
              </Button>
              <Button
                className="cancel-btn"
                onClick={handleCancel}
                disabled={isSaving}
              >
                <CancelOutlinedIcon />
                Cancel
              </Button>
            </div>
          )
        )}
        <h5>Industry and expected exit</h5>
        <div className="client-review-tiles">
          <ReviewTile
            label="Valuation date"
            data={valuationDate}
            isEditing={isEditing}
            alwaysShow={isEditing}
            editField={(
              <EditDateField
                label="Valuation date"
                value={valuationDate}
                onChange={setValuationDate}
              />
            )}
          />
          <ReviewTile
            label="Company industry"
            data={industryVertical?.title}
            isEditing={isEditing}
            alwaysShow={isEditing}
            editField={(
              <EditIndustryField
                label="Company industry"
                value={industryVertical}
                onChange={setIndustryVertical}
                options={industryVerticalsAlphabetizedAndSorted}
              />
            )}
          />
          <ReviewTile
            label="Most likely type of exit event"
            data={whatTypeOfExit}
            isEditing={isEditing}
            alwaysShow={isEditing}
            editField={(
              <EditSelectField
                label="Most likely type of exit event"
                value={whatTypeOfExit}
                onChange={(e) => setWhatTypeOfExit(e.target.value)}
                options={EXIT_TYPE_OPTIONS}
              />
            )}
          />
          <ReviewTile
            label="Most likely timing of exit event"
            data={whenIsExitLikelyToHappen}
            isEditing={isEditing}
            alwaysShow={isEditing}
            editField={(
              <EditSelectField
                label="Most likely timing of exit event"
                value={whenIsExitLikelyToHappen}
                onChange={(e) => setWhenIsExitLikelyToHappen(e.target.value)}
                options={EXIT_TIME_OPTIONS}
              />
            )}
          />
          <ReviewTile
            label="Fiscal year end"
            data={fiscalYearEnd || 'N/A'}
            isEditing={isEditing}
            alwaysShow={isEditing}
            editField={(
              <EditDateField
                label="Fiscal year end"
                value={fiscalYearEnd}
                onChange={setFiscalYearEnd}
              />
            )}
          />
        </div>
      </div>
      <hr />
      <div className="client-review-block">
        <h5>Financials and option pool</h5>
        <div className="client-review-tiles">
          <ReviewTile
            label="Total current cash"
            data={wiz2Data.totalCurrentCash && `$${wiz2Data.totalCurrentCash.toString()}`}
            isEditing={isEditing}
            alwaysShow={isEditing}
            editField={(
              <EditTextField
                label="Total current cash"
                value={wiz2Data.totalCurrentCash}
                onChange={(e) => setWiz2Data({ ...wiz2Data, totalCurrentCash: e.target.value })}
              />
            )}
          />
          <ReviewTile
            label="Current monthly cash burn"
            data={wiz2Data.currentMonthlyCashBurn && `$${wiz2Data.currentMonthlyCashBurn.toString()}`}
            isEditing={isEditing}
            alwaysShow={isEditing}
            editField={(
              <EditTextField
                label="Current monthly cash burn"
                value={wiz2Data.currentMonthlyCashBurn}
                onChange={(e) => setWiz2Data({ ...wiz2Data, currentMonthlyCashBurn: e.target.value })}
              />
            )}
          />
          <ReviewTile
            label="Total non-convertible debt"
            data={wiz2Data.totalNonConvertibleDebt && `$${wiz2Data.totalNonConvertibleDebt.toString()}`}
            isEditing={isEditing}
            alwaysShow={isEditing}
            editField={(
              <EditTextField
                label="Total non-convertible debt"
                value={wiz2Data.totalNonConvertibleDebt}
                onChange={(e) => setWiz2Data({ ...wiz2Data, totalNonConvertibleDebt: e.target.value })}
              />
            )}
          />
          <ReviewTile
            label="Total convertible debt"
            data={wiz2Data.totalConvertibleDebt && `$${wiz2Data.totalConvertibleDebt.toString()}`}
            isEditing={isEditing}
            alwaysShow={isEditing}
            editField={(
              <EditTextField
                label="Total convertible debt"
                value={wiz2Data.totalConvertibleDebt}
                onChange={(e) => setWiz2Data({ ...wiz2Data, totalConvertibleDebt: e.target.value })}
              />
            )}
          />
          <ReviewTile
            label="Remaining option pool"
            data={wiz2Data.ntmOptionsPerc && `${wiz2Data.ntmOptionsPerc.toString()}%`}
            isEditing={isEditing}
            alwaysShow={isEditing}
            editField={(
              <EditTextField
                label="Remaining option pool"
                value={wiz2Data.ntmOptionsPerc}
                onChange={(e) => setWiz2Data({ ...wiz2Data, ntmOptionsPerc: e.target.value })}
              />
            )}
          />
          <ReviewTile
            label="Total current fiscal year revenue"
            data={
              wiz2Data.totalCurrentFiscalYearRevenue ?
                `${wiz2Data.totalCurrentFiscalYearRevenue.toString()}` :
                'N/A'
            }
            isEditing={isEditing}
            alwaysShow={isEditing}
            editField={(
              <EditTextField
                label="Total current fiscal year revenue"
                value={wiz2Data.totalCurrentFiscalYearRevenue}
                onChange={(e) => setWiz2Data({ ...wiz2Data, totalCurrentFiscalYearRevenue: e.target.value })}
              />
            )}
          />
          <ReviewTile
            label="Total next fiscal year revenue"
            data={
              wiz2Data.totalNextFiscalYearRevenue ?
                `${wiz2Data.totalNextFiscalYearRevenue.toString()}` :
                'N/A'
            }
            isEditing={isEditing}
            alwaysShow={isEditing}
            editField={(
              <EditTextField
                label="Total next fiscal year revenue"
                value={wiz2Data.totalNextFiscalYearRevenue}
                onChange={(e) => setWiz2Data({ ...wiz2Data, totalNextFiscalYearRevenue: e.target.value })}
              />
            )}
          />
          <ReviewTile
            label="Total current fiscal year EBITDA"
            data={
              wiz2Data.totalCurrentFiscalYearEBITDA ?
                `${wiz2Data.totalCurrentFiscalYearEBITDA.toString()}` :
                'N/A'
            }
            isEditing={isEditing}
            alwaysShow={isEditing}
            editField={(
              <EditTextField
                label="Total current fiscal year EBITDA"
                value={wiz2Data.totalCurrentFiscalYearEBITDA}
                onChange={(e) => setWiz2Data({ ...wiz2Data, totalCurrentFiscalYearEBITDA: e.target.value })}
              />
            )}
          />
          <ReviewTile
            label="Total next fiscal year EBITDA"
            data={
              wiz2Data.totalNextFiscalYearEBITDA ?
                `${wiz2Data.totalNextFiscalYearEBITDA.toString()}` :
                'N/A'
            }
            isEditing={isEditing}
            alwaysShow={isEditing}
            editField={(
              <EditTextField
                label="Total next fiscal year EBITDA"
                value={wiz2Data.totalNextFiscalYearEBITDA}
                onChange={(e) => setWiz2Data({ ...wiz2Data, totalNextFiscalYearEBITDA: e.target.value })}
              />
            )}
          />
          <ReviewTile
            label="Last 12 months revenue"
            data={
              wiz2Data.ltmRevenue ?
                `${wiz2Data.ltmRevenue.toString()}` :
                'N/A'
            }
            isEditing={isEditing}
            alwaysShow={isEditing}
            editField={(
              <EditTextField
                label="Last 12 months revenue"
                value={wiz2Data.ltmRevenue}
                onChange={(e) => setWiz2Data({ ...wiz2Data, ltmRevenue: e.target.value })}
              />
            )}
          />
          <ReviewTile
            label="Next 12 months revenue"
            data={
              wiz2Data.ntmRevenue ?
                `${wiz2Data.ntmRevenue.toString()}` :
                'N/A'
            }
            isEditing={isEditing}
            alwaysShow={isEditing}
            editField={(
              <EditTextField
                label="Next 12 months revenue"
                value={wiz2Data.ntmRevenue}
                onChange={(e) => setWiz2Data({ ...wiz2Data, ntmRevenue: e.target.value })}
              />
            )}
          />
          <ReviewTile
            label="Last 12 months EBITDA"
            data={
              wiz2Data.ltmEBITDA ?
                `${wiz2Data.ltmEBITDA.toString()}` :
                'N/A'
            }
            isEditing={isEditing}
            alwaysShow={isEditing}
            editField={(
              <EditTextField
                label="Last 12 months EBITDA"
                value={wiz2Data.ltmEBITDA}
                onChange={(e) => setWiz2Data({ ...wiz2Data, ltmEBITDA: e.target.value })}
              />
            )}
          />
          <ReviewTile
            label="Next 12 months EBITDA"
            data={
              wiz2Data.ntmEBITDA ?
                `${wiz2Data.ntmEBITDA.toString()}` :
                'N/A'
            }
            isEditing={isEditing}
            alwaysShow={isEditing}
            editField={(
              <EditTextField
                label="Next 12 months EBITDA"
                value={wiz2Data.ntmEBITDA}
                onChange={(e) => setWiz2Data({ ...wiz2Data, ntmEBITDA: e.target.value })}
              />
            )}
          />
        </div>
      </div>
      <hr />
      <div className="client-review-block">
        <h5>Funding and secondary transactions</h5>
        <div className="client-review-tiles">
          <ReviewTile
            label="Total amount raised"
            data={totalAmountRaised && `$${totalAmountRaised}`}
            isEditing={isEditing}
            alwaysShow={isEditing}
            editField={(
              <EditTextField
                label="Total amount raised"
                value={totalAmountRaised}
                onChange={(e) => setTotalAmountRaised(e.target.value)}
              />
            )}
          />
          <ReviewTile
            label="Current funding type"
            data={chosenFunding}
            isEditing={isEditing}
            alwaysShow={isEditing}
            editField={(
              <EditSelectField
                label="Current funding type"
                value={chosenFunding}
                onChange={(e) => setChosenFunding(e.target.value)}
                options={FUNDING_TYPE_OPTIONS}
              />
            )}
          />
          <ReviewTile
            label="Date of current round of funding"
            data={currentFundingDate || 'N/A'}
            isEditing={isEditing}
            alwaysShow={isEditing}
            editField={(
              <EditDateField
                label="Date of current round of funding"
                value={currentFundingDate}
                onChange={setCurrentFundingDate}
              />
            )}
          />
          {(chosenFunding === 'SAFE Note' || chosenFunding === 'Convertible Note') && (
            <>
              <ReviewTile
                label="Total amount raised"
                data={totalSafeOrConvertible}
                isEditing={isEditing}
                alwaysShow={isEditing}
                editField={(
                  <EditTextField
                    label="Total amount raised"
                    value={totalSafeOrConvertible}
                    onChange={(e) => setTotalSafeOrConvertible(e.target.value)}
                  />
                )}
              />
              <ReviewTile
                label="Interest rate"
                data={interestRate}
                isEditing={isEditing}
                alwaysShow={isEditing}
                editField={(
                  <EditTextField
                    label="Interest rate"
                    value={interestRate}
                    onChange={(e) => setInterestRate(e.target.value)}
                  />
                )}
              />
              <ReviewTile
                label="Valuation Cap"
                data={valuationCap}
                isEditing={isEditing}
                alwaysShow={isEditing}
                editField={(
                  <EditTextField
                    label="Valuation Cap"
                    value={valuationCap}
                    onChange={(e) => setValuationCap(e.target.value)}
                  />
                )}
              />
              <ReviewTile
                label="Pre or Post money"
                data={preOrPostMoneyNotes}
                isEditing={isEditing}
                alwaysShow={isEditing}
                editField={(
                  <EditRadioField
                    label="Pre or Post money"
                    value={preOrPostMoneyNotes || ''}
                    onChange={setPreOrPostMoneyNotes}
                    options={PRE_OR_POST_OPTIONS}
                  />
                )}
              />
            </>
          )}
          <ReviewTile
            label="Has the company received any term sheets for future rounds of funding?"
            data={yesNoValue(receivedFutureTerms)}
            isEditing={isEditing}
            alwaysShow={isEditing}
            editField={(
              <EditYesNoField
                label="Has the company received any term sheets for future rounds of funding?"
                value={receivedFutureTerms}
                onChange={setReceivedFutureTerms}
              />
            )}
          />
          <ReviewTile
            label="Has the company had any secondary common stock transactions?"
            data={yesNoValue(anySecondaryCommonStockTransactions)}
            isEditing={isEditing}
            alwaysShow={isEditing}
            editField={(
              <EditYesNoField
                label="Has the company had any secondary common stock transactions?"
                value={anySecondaryCommonStockTransactions}
                onChange={setAnySecondaryCommonStockTransactions}
              />
            )}
          />
          {anySecondaryCommonStockTransactions === true && (
            <>
              <ReviewTile
                label="Total amount of secondary stock transaction"
                data={secondaryCommonStockTransactionTotalAmount}
                isEditing={isEditing}
                alwaysShow={isEditing}
                editField={(
                  <EditTextField
                    label="Total amount of secondary stock transaction"
                    value={secondaryCommonStockTransactionTotalAmount}
                    onChange={(e) => setSecondaryCommonStockTransactionTotalAmount(e.target.value)}
                  />
                )}
              />
              <ReviewTile
                label="Percent of common stock sold"
                data={(
                  percentageSecondaryCommonStockSold.length === 0 ?
                    '' :
                    `${percentageSecondaryCommonStockSold}%`
                )}
                isEditing={isEditing}
                alwaysShow={isEditing}
                editField={(
                  <EditTextField
                    label="Percent of common stock sold"
                    value={percentageSecondaryCommonStockSold}
                    onChange={(e) => setPercentageSecondaryCommonStockSold(e.target.value)}
                  />
                )}
              />
            </>
          )}
        </div>
      </div>
      <hr />
      {(capTableProvider || capTableFilesUploaded?.length || articlesOfIncorporationUploaded?.length ||
        secondaryTransactionDocs?.length || safeOrConvertibleNoteAgreement?.length || termsSheets) &&
        (
          <div className="client-review-block">
            <h5>Cap table and upload</h5>
            <div className="client-review-tiles">
              <ReviewTile
                label="Who's your Cap table provider?"
                data={capTableProvider}
                isEditing={isEditing}
                alwaysShow={isEditing}
                editField={(
                  <EditSelectField
                    label="Who's your Cap table provider?"
                    value={capTableProvider}
                    onChange={(e) => setCapTableProvider(e.target.value)}
                    options={CAP_TABLE_PROVIDER_OPTIONS}
                  />
                )}
              />
              <ReviewTile
                label="File upload: Cap Table provider"
                data={capTableFilesUploaded}
              />
              <ReviewTile
                label="File upload: Option Ledger"
                data={optionLedgerFilesUploaded}
              />
              <ReviewTile
                label="File upload: Financials"
                data={financialFilesUpload}
              />
              <ReviewTile
                label="File upload: Articles of incorporation"
                data={articlesOfIncorporationUploaded}
              />
              {
                anySecondaryCommonStockTransactions && (
                  <ReviewTile
                    label="File upload: Secondary transaction documents"
                    data={secondaryTransactionDocs}
                  />
                )
              }
              {
                safeOrConvertibleForCurrentOrPreviousRounds && (
                  <ReviewTile
                    label="File upload: SAFE or convertible note agreement documents"
                    data={safeOrConvertibleNoteAgreement}
                  />
                )
              }
              {
                receivedFutureTerms && (
                  <ReviewTile
                    label="File upload: Future terms sheets documents"
                    data={termsSheets}
                  />
                )
              }
            </div>
          </div>
        )}
    </div>
  );
}

ClientInputs.propTypes = {
  companyToViewData: PropTypes.object,
  setCompanyToViewData: PropTypes.func,
  currentAdminName: PropTypes.string,
};
